import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input, List, message, Spin } from 'antd';

import { ContractComment, EmptyPanel } from '~/components';
import { PanelTypes, PanelKindTypes } from '~/components/EmptyPanel/EmptyPanel';

import getContractComments, {
  GET_CONTRACT_COMMENTS_REQUEST,
  GET_CONTRACT_COMMENTS_SUCCESS,
  GET_CONTRACT_COMMENTS_FAILURE,
} from '~/store/getContractComments/action';
import sendContractComment, {
  SEND_CONTRACT_COMMENT_REQUEST,
  SEND_CONTRACT_COMMENT_SUCCESS,
  SEND_CONTRACT_COMMENT_FAILURE,
} from '~/store/sendContractComment/action';
import { Container, Label, ListWrapper, SendButton } from './Comments.styled';

type CommentsProps = {
  contractId: string;
};

type ContractCommentItem = {
  commentaryId: string;
  userName: string;
  commentary: string;
  contractStatus: string;
  commentaryDate: string;
};

export function Comments({ contractId }: CommentsProps) {
  const dispatch = useDispatch();
  const commentsList = useSelector((state: any) => state.getIn(['getContractComments']));
  const sendCommentState = useSelector((state: any) => state.getIn(['sendContractComment']));

  const [comment, setComment] = useState('');
  const [comments, setComments] = useState<ContractCommentItem[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [forcedForm, setForcedForm] = useState(false);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    setComments([]);
    setForcedForm(false);
    setIsLoadingComments(false);
    dispatch(getContractComments(contractId));
  }, [contractId]);

  useEffect(() => {
    if (!commentsList) return;
    const type = commentsList.getIn(['type']);
    switch (type) {
      case GET_CONTRACT_COMMENTS_REQUEST:
        setIsLoadingComments(true);
        break;
      case GET_CONTRACT_COMMENTS_SUCCESS: {
        const payload = commentsList.getIn(['payload']);
        const { commentaries } = payload || {};
        setIsLoadingComments(false);
        setComments(commentaries ?? []);
        break;
      }
      case GET_CONTRACT_COMMENTS_FAILURE:
        setIsLoadingComments(false);
        message.error('Não foi possível carregar os comentários deste contrato!');
        break;
      default:
        break;
    }
  }, [commentsList]);

  useEffect(() => {
    if (!sendCommentState) return;
    const type = sendCommentState.getIn(['type']);
    switch (type) {
      case SEND_CONTRACT_COMMENT_REQUEST:
        setIsSending(true);
        break;
      case SEND_CONTRACT_COMMENT_SUCCESS:
        setIsSending(false);
        message.success('Comentário enviado com sucesso!');
        dispatch(getContractComments(contractId));
        break;
      case SEND_CONTRACT_COMMENT_FAILURE:
        setIsSending(false);
        message.error('Não foi possível enviar o seu comentário!');
        break;
      default:
        break;
    }
  }, [sendCommentState, contractId]);

  const handleSubmit = () => {
    dispatch(sendContractComment(contractId, comment));
    setComment('');
  };

  const isAtLimit = comment.length >= 700;
  const showForm = comments.length > 0 || forcedForm;

  return (
    <Spin spinning={isLoadingComments}>
      <Container>
        {showForm ? (
          <>
            <Label>Comentar:</Label>
            <Form.Item
              validateStatus={isAtLimit ? 'error' : ''}
              help={isAtLimit ? 'Limite de 700 caracteres atingido. Reduza o texto para continuar.' : null}
            >
              <Input.TextArea
                rows={3}
                value={comment}
                placeholder="Escreva um comentário para esse contrato"
                onChange={(ev) => setComment(ev.target.value)}
                maxLength={700}
              />
            </Form.Item>
            <SendButton
              type="primary"
              disabled={!comment || isAtLimit || isSending}
              loading={isSending}
              onClick={handleSubmit}
            >
              Enviar
            </SendButton>
            <ListWrapper>
              <List
                rowKey="commentaryId"
                dataSource={comments}
                renderItem={(item) => <ContractComment key={item.commentaryId} comment={item} />}
              />
            </ListWrapper>
          </>
        ) : (
          <EmptyPanel
            btnAction={() => setForcedForm(true)}
            panel={PanelTypes.CONTRACT as 'contract'}
            kind={PanelKindTypes.CONTRACT_COMMENT}
          />
        )}
      </Container>
    </Spin>
  );
}
